import React from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck,
  HeartHandshake,
  Users,
  ArrowRight,
  Phone,
  CheckCircle2,
  Sparkles
} from "lucide-react";
import { Button } from "../components/ui/button";
import { services, trustIndicators, testimonials, companyInfo } from "../data/mockData";
import ServiceCard from "../components/ServiceCard";
import TestimonialCard from "../components/TestimonialCard";
import SectionHeader from "../components/SectionHeader";
import ChecklistSection from "../components/ChecklistSection";

const iconMap = { ShieldCheck, HeartHandshake, Users };

const Home = () => {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 lg:pt-16 pb-20 lg:pb-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            {/* Left copy */}
            <div className="lg:col-span-6 animate-fade-up">
              <div className="inline-flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-full px-4 py-2 mb-8">
                <Sparkles className="w-4 h-4 text-amber-700" />
                <span className="text-xs font-semibold tracking-widest uppercase text-amber-800">
                  Compassion. Respect. Trust.
                </span>
              </div>

              <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-slate-900 leading-[1.05] tracking-tight">
                Care that feels{" "}
                <span className="italic text-amber-700">like family.</span>
              </h1>

              <p className="mt-8 text-lg lg:text-xl text-slate-600 leading-relaxed max-w-xl">
                {companyInfo.mission}
              </p>

              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button
                    data-testid="hero-primary-cta"
                    size="lg"
                    className="bg-amber-700 hover:bg-amber-800 text-white rounded-full px-8 py-6 text-base font-medium w-full sm:w-auto"
                  >
                    Request a Free Consultation
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <a href={`tel:${companyInfo.phone.replace(/\D/g, "")}`}>
                  <Button
                    data-testid="hero-secondary-cta"
                    variant="outline"
                    size="lg"
                    className="border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white rounded-full px-8 py-6 text-base font-medium w-full sm:w-auto"
                  >
                    <Phone className="w-4 h-4 mr-2" />
                    {companyInfo.phone}
                  </Button>
                </a>
              </div>

              <div className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-3">
                {["Background-checked", "Fully insured", "24/7 available"].map((item) => (
                  <div key={item} className="flex items-center gap-2 text-sm text-slate-700">
                    <CheckCircle2 className="w-4 h-4 text-amber-700" />
                    <span className="font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right image */}
            <div className="lg:col-span-6 relative animate-fade-in delay-200">
              <div className="relative aspect-[4/5] rounded-[2.5rem] overflow-hidden bg-amber-50">
                <img
                  src="https://images.unsplash.com/photo-1765896387387-0538bc9f997e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwxfHxjYXJlZ2l2ZXIlMjBzZW5pb3IlMjBwYXRpZW50JTIwc21pbGluZ3xlbnwwfHx8fDE3NzgwMTE0NTh8MA&ixlib=rb-4.1.0&q=85"
                  alt="Caregiver with senior client"
                  className="w-full h-full object-cover"
                  data-testid="hero-image"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/30 via-transparent to-transparent" />
              </div>

              {/* Floating trust badge */}
              <div className="absolute -bottom-6 -left-6 lg:-left-10 bg-white rounded-2xl shadow-xl p-6 max-w-[240px] border border-stone-200/60">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-full bg-amber-700 flex items-center justify-center">
                    <HeartHandshake className="w-5 h-5 text-white" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="font-serif text-2xl text-slate-900 leading-none">500+</p>
                    <p className="text-xs text-slate-500 mt-1">Families served</p>
                  </div>
                </div>
                <p className="text-xs text-slate-600 mt-3">
                  With loving care, every day.
                </p>
              </div>

              {/* Promise badge */}
              <div className="absolute -top-4 -right-4 lg:-right-8 bg-amber-700 text-white rounded-full w-32 h-32 flex flex-col items-center justify-center text-center p-4 shadow-xl">
                <span className="text-[10px] uppercase tracking-[0.2em]">Compassion</span>
                <span className="text-[10px] uppercase tracking-[0.2em]">Respect</span>
                <span className="text-[10px] uppercase tracking-[0.2em]">Trust</span>
              </div>
            </div>
          </div>
        </div>

        {/* Leaf ornament */}
        <div className="absolute left-0 top-1/3 w-32 h-32 opacity-20 text-amber-700 pointer-events-none hidden lg:block">
          <svg viewBox="0 0 100 100" fill="currentColor">
            <path d="M50 10 C 30 30, 20 50, 30 80 C 40 60, 60 50, 70 30 Z" />
          </svg>
        </div>
      </section>

      {/* TRUST INDICATORS */}
      <section className="bg-slate-900 text-white py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-6">
            {trustIndicators.map((item, i) => {
              const Icon = iconMap[item.icon];
              return (
                <div
                  key={item.title}
                  data-testid={`trust-indicator-${i}`}
                  className="text-center flex flex-col items-center border-r border-stone-800 last:border-r-0 md:px-4"
                >
                  <div className="w-16 h-16 rounded-full border-2 border-amber-500/30 flex items-center justify-center mb-5">
                    <Icon className="w-7 h-7 text-amber-400" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-serif text-xl text-amber-400 uppercase tracking-widest mb-3">
                    {item.title}
                  </h3>
                  <p className="text-stone-300 text-base max-w-xs">
                    {item.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-20 lg:py-32 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16">
            <div className="max-w-2xl">
              <p className="text-amber-700 text-sm uppercase tracking-[0.3em] font-semibold mb-4">
                Our Companion Care Services
              </p>
              <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-slate-900 leading-tight tracking-tight">
                Gentle support for every moment of the day.
              </h2>
            </div>
            <Link to="/services" className="hidden lg:inline-flex">
              <Button
                variant="outline"
                data-testid="services-view-all"
                className="border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white rounded-full px-7 py-5 font-medium"
              >
                View all services
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {services.map((service, i) => (
              <ServiceCard key={service.id} service={service} index={i} />
            ))}
          </div>

          <div className="mt-10 flex lg:hidden">
            <Link to="/services" className="w-full">
              <Button
                variant="outline"
                className="w-full border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white rounded-full py-6"
              >
                View all services
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* PROMISE */}
      <section className="py-20 lg:py-32 bg-amber-50/40">        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="relative order-2 lg:order-1">
              <div className="aspect-[4/5] rounded-[2.5rem] overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1750853730688-61ba53660e0d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwzfHxjYXJlZ2l2ZXIlMjBzZW5pb3IlMjBwYXRpZW50JTIwc21pbGluZ3xlbnwwfHx8fDE3NzgwMTE0NTh8MA&ixlib=rb-4.1.0&q=85"
                  alt="Happy senior man"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 lg:-right-10 bg-white rounded-3xl shadow-xl p-6 max-w-xs border border-stone-200/60">
                <p className="font-script text-3xl text-amber-700 mb-2">Our Promise</p>
                <p className="text-sm text-slate-700 leading-relaxed">
                  {companyInfo.promise}
                </p>
              </div>
            </div>

            <div className="order-1 lg:order-2">
              <p className="text-amber-700 text-sm uppercase tracking-[0.3em] font-semibold mb-4">
                Why families choose us
              </p>
              <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-slate-900 leading-tight tracking-tight mb-8">
                Where <span className="italic text-amber-700">peace of mind</span> lives at home.
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-8">
                When you welcome us into your loved one's home, we don't arrive with a checklist. We arrive with an open heart. Every gesture — from a warm meal to a shared memory — is offered with patience, kindness, and dignity.
              </p>
              <div className="space-y-5">
                {[
                  "Personalized care plans built around your family",
                  "Carefully matched, background-checked caregivers",
                  "Consistent communication and regular family updates",
                  "Flexible hours — a few hours a week to 24/7 support"
                ].map((point, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="w-6 h-6 rounded-full bg-amber-700 flex items-center justify-center flex-shrink-0 mt-1">
                      <CheckCircle2 className="w-4 h-4 text-white" />
                    </div>
                    <p className="text-slate-700 leading-relaxed">{point}</p>
                  </div>
                ))}
              </div>

              <Link to="/about" className="inline-block mt-10">
                <Button
                  data-testid="about-cta"
                  className="bg-slate-900 hover:bg-slate-800 text-white rounded-full px-8 py-6 font-medium"
                >
                  More about our story
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FREE CHECKLIST LEAD MAGNET */}
      <ChecklistSection />

      {/* TESTIMONIALS PREVIEW */}
      <section className="py-20 lg:py-32 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader
            eyebrow="What families are saying"
            title="Stories that move us forward"
            description="Every family has a different story. We are honored to be part of theirs."
          />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mt-16">
            {testimonials.slice(0, 3).map((t) => (
              <TestimonialCard key={t.id} testimonial={t} />
            ))}
          </div>

          <div className="mt-14 text-center">
            <Link to="/testimonials">
              <Button
                variant="outline"
                data-testid="testimonials-view-all"
                className="border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white rounded-full px-8 py-6"
              >
                Read more stories
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-60 h-60 rounded-full bg-amber-500/30 blur-3xl" />
          <div className="absolute bottom-10 right-10 w-80 h-80 rounded-full bg-amber-400/20 blur-3xl" />
        </div>
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32 text-center">
          <p className="font-script text-4xl text-amber-400 mb-6">We're here to help</p>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white leading-tight tracking-tight mb-8">
            Let's begin the conversation.
          </h2>
          <p className="text-stone-300 text-lg leading-relaxed max-w-2xl mx-auto mb-10">
            Every family starts with a single call. There's no obligation and no pressure — just a warm, thoughtful conversation about your loved one's needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`tel:${companyInfo.phone.replace(/\D/g, "")}`}>
              <Button
                data-testid="footer-cta-phone"
                size="lg"
                className="bg-amber-600 hover:bg-amber-700 text-white rounded-full px-8 py-6 text-base w-full sm:w-auto"
              >
                <Phone className="w-4 h-4 mr-2" />
                Call {companyInfo.phone}
              </Button>
            </a>
            <Link to="/contact">
              <Button
                data-testid="footer-cta-form"
                variant="outline"
                size="lg"
                className="border-2 border-white text-white hover:bg-white hover:text-slate-900 rounded-full px-8 py-6 text-base bg-transparent w-full sm:w-auto"
              >
                Request care online
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
